import { useState, useCallback, useRef, useEffect } from "react";
import { evaluationApi } from "@services/evaluationApi";
import { notificationApi } from "@services/notificationApi";
import { ProcessStatus } from "@models/extraction";
import type { INotificationMessage } from "@models/extraction";
import type {
  EvaluationListViewModel,
  EvaluationDetailViewModel,
} from "@models/evaluation";
import { useBrowserNotifications } from "@hooks/useBrowserNotifications";

interface UseEvaluationProps {
  doctypeId: string;
  flowBaseId: string;
  flowVersion: number;
}

interface IEvaluationState {
  // List State
  evaluationList: EvaluationListViewModel | null;
  isLoadingList: boolean;

  // Detail State
  evaluationDetails: EvaluationDetailViewModel[];
  isLoadingDetail: boolean;

  // Trigger State
  isTriggeringEvaluation: boolean;
  evaluationInProgress: boolean;
  evaluationStatus: string;
  currentProcessId: string | null;

  // Error State
  error: string | null;

  // Methods
  triggerEvaluation: () => Promise<void>;
  loadEvaluations: (lastKey?: string) => Promise<void>;
  loadEvaluationDetail: (processId: string) => Promise<void>;
  refreshEvaluations: () => Promise<void>;
  clearError: () => void;
}

export const useEvaluation = ({
  doctypeId,
  flowBaseId,
  flowVersion,
}: UseEvaluationProps): IEvaluationState => {
  const [evaluationList, setEvaluationList] =
    useState<EvaluationListViewModel | null>(null);
  const [evaluationDetails, setEvaluationDetails] = useState<
    EvaluationDetailViewModel[]
  >([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isTriggeringEvaluation, setIsTriggeringEvaluation] = useState(false);
  const [evaluationInProgress, setEvaluationInProgress] = useState(false);
  const [evaluationStatus, setEvaluationStatus] = useState("");
  const [currentProcessId, setCurrentProcessId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activeWebSocketRef = useRef<string | null>(null);
  const notifiedProcessIdRef = useRef(false);
  const { showNotification } = useBrowserNotifications();

  const loadEvaluations = useCallback(
    async (lastKey?: string) => {
      setIsLoadingList(true);
      setError(null);
      try {
        const response = await evaluationApi.listEvaluations(
          doctypeId,
          flowBaseId,
          flowVersion,
          20,
          lastKey,
        );
        setEvaluationList(response);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load evaluations";
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoadingList(false);
      }
    },
    [doctypeId, flowBaseId, flowVersion],
  );

  const loadEvaluationDetail = useCallback(async (processId: string) => {
    setIsLoadingDetail(true);
    setError(null);
    try {
      const details = await evaluationApi.getEvaluationDetails(processId);
      setEvaluationDetails(details);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to load evaluation details";
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  const refreshEvaluations = useCallback(async () => {
    await loadEvaluations();
  }, [loadEvaluations]);

  const handleEvaluationNotification = useCallback(
    async (processId: string, message: INotificationMessage) => {
      if (message.status) {
        switch (message.status) {
          case ProcessStatus.Pending:
            setEvaluationStatus("Evaluation pending...");
            break;
          case ProcessStatus.BatchCreating:
            setEvaluationStatus("Creating evaluation batch...");
            break;
          case ProcessStatus.Processing:
            setEvaluationStatus("Processing evaluation...");
            break;
          case ProcessStatus.Completed:
          case ProcessStatus.PartialCompleted:
            setEvaluationStatus("Evaluation completed!");
            setEvaluationInProgress(false);

            if (!notifiedProcessIdRef.current) {
              showNotification("Evaluation Completed", {
                body: "The evaluation process has finished successfully.",
                tag: processId,
              });
              notifiedProcessIdRef.current = true;
            }

            await refreshEvaluations();

            setTimeout(() => {
              notificationApi.disconnect(processId);
              activeWebSocketRef.current = null;
              setCurrentProcessId(null);
            }, 2000);
            break;
          case ProcessStatus.Failed:
            setEvaluationStatus("Evaluation failed");
            setEvaluationInProgress(false);
            setError("Evaluation process failed");

            if (!notifiedProcessIdRef.current) {
              showNotification("Evaluation Failed", {
                body: "The evaluation process encountered an error.",
                tag: processId,
              });
              notifiedProcessIdRef.current = true;
            }

            notificationApi.disconnect(processId);
            activeWebSocketRef.current = null;
            setCurrentProcessId(null);
            break;
          default:
            setEvaluationStatus(`Status: ${message.status}`);
        }
      }
    },
    [refreshEvaluations, showNotification],
  );

  const connectToEvaluationNotifications = useCallback(
    (processId: string) => {
      if (activeWebSocketRef.current === processId) {
        return;
      }

      notificationApi.connectToNotifications(doctypeId, processId, {
        onMessage: (message) => {
          handleEvaluationNotification(processId, message);
        },
        onError: (error) => {
          console.error("Evaluation WebSocket error:", error);
          activeWebSocketRef.current = null;
        },
        onClose: () => {
          activeWebSocketRef.current = null;
        },
        onOpen: () => {
          activeWebSocketRef.current = processId;
        },
      });
    },
    [doctypeId, handleEvaluationNotification],
  );

  const triggerEvaluation = useCallback(async () => {
    setIsTriggeringEvaluation(true);
    setError(null);
    notifiedProcessIdRef.current = false;

    try {
      const response = await evaluationApi.triggerEvaluation(
        doctypeId,
        flowBaseId,
        flowVersion,
      );

      setCurrentProcessId(response.processId);
      setEvaluationInProgress(true);
      setEvaluationStatus("Evaluation started...");
      connectToEvaluationNotifications(response.processId);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to trigger evaluation";
      setError(errorMessage);
      throw err;
    } finally {
      setIsTriggeringEvaluation(false);
    }
  }, [doctypeId, connectToEvaluationNotifications]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      if (activeWebSocketRef.current) {
        notificationApi.disconnect(activeWebSocketRef.current);
      }
    };
  }, []);

  return {
    evaluationList,
    evaluationDetails,
    isLoadingList,
    isLoadingDetail,
    isTriggeringEvaluation,
    evaluationInProgress,
    evaluationStatus,
    currentProcessId,
    error,
    triggerEvaluation,
    loadEvaluations,
    loadEvaluationDetail,
    refreshEvaluations,
    clearError,
  };
};
